import React from 'react';
import { FileCheck } from 'lucide-react';
import { Button } from '../../components/ui';
import type { WorkflowExecutionObservation } from '../../generated/api/types.gen.js';
import { formatDate, schemaFieldNames } from './stepDebugOutput.utils.js';

export const ExecutionObservationSection: React.FC<{
  observation: WorkflowExecutionObservation;
  onPinAsContract?: (contract: {
    jsonSchema: Record<string, unknown>;
    strict: boolean;
    example?: Record<string, unknown>;
  }) => void;
}> = ({ observation, onPinAsContract }) => {
  const schema = observation.observedSchema;
  const diff = observation.diff;
  const added = diff.addedFields;
  const removed = diff.removedFields;
  const changed = diff.changedTypes;
  const fields = schemaFieldNames(schema);

  return (
    <div className="rounded-sm border bg-surface-alt px-3 py-2">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <div className="text-xs font-medium text-heading">Observed from workflow runs</div>
          <div className="text-[10px] text-muted">
            {observation.sampleCount ?? 0} sample{observation.sampleCount === 1 ? '' : 's'}
            {observation.latestCompletedAt ? `, latest ${formatDate(observation.latestCompletedAt)}` : ''}
          </div>
        </div>
        {schema && onPinAsContract && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPinAsContract({ jsonSchema: schema, strict: true })}
            title="Use the observed workflow-run shape as the step output contract"
          >
            <FileCheck size={10} />
            Pin observed
          </Button>
        )}
      </div>
      {fields.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {fields.slice(0, 16).map((field) => (
            <span key={field} className="rounded-sm bg-surface px-1.5 py-0.5 font-mono text-[10px] text-muted">
              {field}
            </span>
          ))}
          {fields.length > 16 && <span className="text-[10px] text-faint">+{fields.length - 16} more</span>}
        </div>
      )}
      {(added.length > 0 || removed.length > 0 || changed.length > 0) && (
        <div className="grid gap-1 text-[10px] text-muted">
          {added.length > 0 && <div>New fields: {added.slice(0, 6).join(', ')}</div>}
          {removed.length > 0 && <div className="text-warning">Missing fields: {removed.slice(0, 6).join(', ')}</div>}
          {changed.length > 0 && (
            <div className="text-warning">
              Type changes:{' '}
              {changed
                .slice(0, 4)
                .map((item) => item.path)
                .join(', ')}
            </div>
          )}
        </div>
      )}
      {observation.verification && (
        <div className="mt-2 text-[10px] text-faint">
          Verification: {observation.verification.passed ?? 0} passed, {observation.verification.failed ?? 0} failed,{' '}
          {observation.verification.warning ?? 0} warnings
        </div>
      )}
    </div>
  );
};
